import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  DownloadOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LatestUploadCard from "../LatestUploadCard";

const API = process.env.REACT_APP_BACKEND_URL;

// File the stored Parts Master came from, for the uploaded-files list.
const getPartSource = (row) => ({
  fileName: row.sourceFileName,
  uploadedAt: row.uploadedAt,
});

// Parts Master template: one row per material.
const HEADERS = ["Material Code", "Material Description", "Lead Time (Days)"];

const normalize = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim();
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const number = Number(String(value).replace(/,/g, "").trim());

  if (Number.isNaN(number)) {
    return null;
  }

  return number;
};

// Headers are matched ignoring letter case and spaces; "Lead Time" without "(Days)" is accepted too.
const headerKey = (value) => normalize(value).toLowerCase().replace(/\s+/g, " ");

const HEADER_KEYS = {
  partCode: ["material code"],
  partDescription: ["material description"],
  leadTimeDays: ["lead time (days)", "lead time"],
};

const findHeaderRow = (rows) => {
  for (let i = 0; i < rows.length; i++) {
    const headers = rows[i].map(headerKey);

    if (
      Object.values(HEADER_KEYS).every((keys) =>
        keys.some((key) => headers.includes(key)),
      )
    ) {
      return i;
    }
  }

  return -1;
};

const UploadPartMaster = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentRows, setCurrentRows] = useState([]);
  const [currentLoading, setCurrentLoading] = useState(true);
  const [currentError, setCurrentError] = useState("");

  const navigate = useNavigate();

  /*
   * Load the Parts Master currently stored
   */
  const loadCurrent = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "null");

    try {
      setCurrentLoading(true);
      setCurrentError("");

      const response = await axios.get(`${API}/part-master`, {
        headers: {
          Authorization: `Bearer ${user?.accessToken}`,
        },
      });

      setCurrentRows(
        (response.data || []).map((row, index) => ({
          ...row,
          id: row.partMasterId || index + 1,
        })),
      );
    } catch (err) {
      setCurrentRows([]);
      setCurrentError(
        err?.response?.data?.Exception ||
          err?.response?.data?.message ||
          "Unable to load the current Parts Master.",
      );
    } finally {
      setCurrentLoading(false);
    }
  };

  useEffect(() => {
    loadCurrent();
  }, []);

  const currentUploadedAt = currentRows.reduce(
    (latest, row) =>
      row.uploadedAt && (!latest || new Date(row.uploadedAt) > new Date(latest))
        ? row.uploadedAt
        : latest,
    null,
  );

  /*
   * Download Excel template
   */
  const downloadTemplate = () => {
    setError("");
    setSuccess("");

    const sheet = XLSX.utils.aoa_to_sheet([HEADERS]);

    sheet["!cols"] = HEADERS.map((header) => ({
      wch: header === "Material Description" ? 40 : 20,
    }));

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, sheet, "Parts Master");

    XLSX.writeFile(workbook, "Parts_Master_Template.xlsx");

    setSuccess("Parts Master template downloaded.");
  };

  /*
   * Read Excel file
   */
  const parseFile = async (selectedFile) => {
    const buffer = await selectedFile.arrayBuffer();

    const workbook = XLSX.read(buffer, {
      type: "array",
      raw: true,
    });

    if (!workbook.SheetNames.length) {
      throw new Error("The Excel file has no sheets.");
    }

    const worksheet = workbook.Sheets[workbook.SheetNames[0]];

    const data = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      raw: true,
    });

    /*
     * Find header row.
     * This also means Sr. No. can exist above/before
     * the actual headers and will be ignored.
     */
    const headerRowIndex = findHeaderRow(data);

    if (headerRowIndex === -1) {
      throw new Error(
        "Header row not found. The file needs the columns Material Code, Material Description and Lead Time (Days).",
      );
    }

    const headers = data[headerRowIndex].map(headerKey);

    const dataRows = data.slice(headerRowIndex + 1);

    const indexOf = (keys) =>
      headers.findIndex((header) => keys.includes(header));

    const partCodeIndex = indexOf(HEADER_KEYS.partCode);
    const descriptionIndex = indexOf(HEADER_KEYS.partDescription);
    const leadTimeIndex = indexOf(HEADER_KEYS.leadTimeDays);

    /*
     * Keep one row per Material Code; a repeated code fills only what is still missing.
     */
    const grouped = new Map();
    const errors = [];

    dataRows.forEach((row, index) => {
      if (!row || row.every((value) => normalize(value) === "")) {
        return;
      }

      const excelRow = headerRowIndex + index + 2;
      const partCode = normalize(row[partCodeIndex]);

      if (!partCode) {
        errors.push(`Row ${excelRow}: Material Code is required.`);
        return;
      }

      const rawLeadTime = normalize(row[leadTimeIndex]);
      const leadTime = toNumber(row[leadTimeIndex]);

      if (rawLeadTime && (leadTime === null || leadTime < 0 || !Number.isInteger(leadTime))) {
        errors.push(
          `Row ${excelRow}: Lead Time "${rawLeadTime}" must be a whole number of days.`,
        );
        return;
      }

      if (!grouped.has(partCode)) {
        grouped.set(partCode, {
          partCode,
          partDescription: "",
          leadTimeDays: null,
        });
      }

      const part = grouped.get(partCode);

      if (!part.partDescription) {
        part.partDescription = normalize(row[descriptionIndex]);
      }

      if (part.leadTimeDays === null && leadTime !== null) {
        part.leadTimeDays = leadTime;
      }
    });

    if (errors.length) {
      throw new Error(errors.slice(0, 10).join("\n"));
    }

    const result = Array.from(grouped.values());

    if (!result.length) {
      throw new Error("No Parts Master data was found in the Excel file.");
    }

    return result;
  };

  /*
   * Excel selection
   */
  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setRows([]);
    setFile(null);

    try {
      const fileName = selectedFile.name.toLowerCase();

      if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
        throw new Error("Please upload an Excel file (.xlsx or .xls).");
      }

      const parsedRows = await parseFile(selectedFile);

      setFile(selectedFile);

      setRows(
        parsedRows.map((row, index) => ({
          ...row,
          id: index + 1,
        })),
      );

      setSuccess(`${parsedRows.length} material records loaded for preview.`);
    } catch (err) {
      setError(err?.message || "Unable to read the Parts Master Excel file.");
    } finally {
      setLoading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  /*
   * Submit to backend
   */
  const handleSubmit = async () => {
    if (!file || rows.length === 0) {
      setError("Please upload a Parts Master file first.");

      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "null");

    if (!user?.accessToken) {
      setError("You are not logged in.");

      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      /*
       * id is only for DataGrid.
       * Do not send it to backend.
       */
      const payload = rows.map(({ id, ...row }) => row);

      const response = await axios.post(
        `${API}/part-master/upload?fileName=${encodeURIComponent(file.name)}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      setSuccess(response.data || "Parts Master uploaded successfully.");

      navigate("/admin");
    } catch (err) {
      setError(
        err?.response?.data?.Exception ||
          err?.response?.data?.message ||
          err?.message ||
          "Parts Master upload failed.",
      );
    } finally {
      setUploading(false);
    }
  };

  /*
   * Clear selected file
   */
  const clear = () => {
    setFile(null);
    setRows([]);
    setError("");
    setSuccess("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /*
   * DataGrid columns
   */
  const columns = useMemo(
    () => [
      {
        field: "partCode",
        headerName: "Material Code",
        minWidth: 140,
        flex: 1,
      },

      {
        field: "partDescription",
        headerName: "Material Description",
        minWidth: 250,
        flex: 2,
      },

      {
        field: "leadTimeDays",
        headerName: "Lead Time (Days)",
        minWidth: 130,
      },
    ],
    [],
  );

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#172033",
          }}
        >
          Parts Master
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Upload the lead time of each material.
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            whiteSpace: "pre-line",
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <LatestUploadCard
          title="Current Parts Master"
          uploadedAt={currentUploadedAt}
          loading={currentLoading}
          error={currentError}
          isEmpty={currentRows.length === 0}
          emptyMessage="No Parts Master has been uploaded yet."
          onRefresh={loadCurrent}
          rows={currentRows}
          columns={columns}
          getSource={getPartSource}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid #e3e7ef",
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
          }}
        >
          Parts Master Excel
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.5,
            mb: 2,
          }}
        >
          Columns: Material Code, Material Description and Lead Time (Days). Each
          upload replaces the current Parts Master. The lead time sets a PO's
          delivery date when the PO report has no PO Delivery Date.
        </Typography>

        {file && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontWeight: 600,
            }}
          >
            Selected file: {file.name}
          </Typography>
        )}

        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={1}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadOutlined />}
            onClick={downloadTemplate}
            disabled={loading || uploading}
          >
            Download Template
          </Button>

          <Button
            variant="outlined"
            startIcon={
              loading ? <CircularProgress size={18} /> : <UploadFileOutlined />
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || uploading}
          >
            {loading ? "Processing..." : "Choose Excel"}
          </Button>

          {rows.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={clear}
              disabled={uploading}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {rows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e3e7ef",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e3e7ef",

              display: "flex",

              justifyContent: "space-between",

              alignItems: {
                xs: "flex-start",
                md: "center",
              },

              gap: 2,

              flexDirection: {
                xs: "column",
                md: "row",
              },
            }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  sx={{
                    fontWeight: 700,
                  }}
                >
                  Parts Master Preview
                </Typography>

                <Chip
                  size="small"
                  label={`${rows.length} materials`}
                  variant="outlined"
                />
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.4,
                }}
              >
                Review the data before replacing the current Parts Master.
              </Typography>
            </Box>

            <Button
              className="btn-contained"
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <UploadFileOutlined />
                )
              }
              onClick={handleSubmit}
              disabled={uploading || loading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
              }}
            >
              {uploading ? "Replacing Master..." : "Submit Parts Master"}
            </Button>
          </Box>

          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                  page: 0,
                },
              },
            }}
            sx={{
              border: 0,

              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f7f9fc",
                fontWeight: 700,
              },

              "& .MuiDataGrid-cell": {
                fontSize: "0.82rem",
              },
            }}
          />
        </Paper>
      )}

    </Box>
  );
};

export default UploadPartMaster;
